import {useContext, useState, useMemo, useCallback} from 'react';

import {useTranslation} from 'react-i18next';

import {Box, Grid, MenuItem, TextField, Typography} from '@mui/material';

import {useMatchesGraph} from '../hooks/useMatchesGraph';

import {LineChart} from './LineChart';
import EmpionLogo from '../../../../../components/EmpionLogoAnimation/EmpionLogo';
import {EmpionPaper} from '../../../../../components/EmpionPaper/EmpionPaper';
import {Loading} from '../../../../../components/Loading/Loading';
import {LanguageContext} from '../../../../../contexts/LanguageContext/LanguageContext';
import {greenColors, orangeColors} from '../../../../../layouts/theme/designSystem';
import {MatchStatus} from '../../../../../types/MatchStatus/MatchStatus';
import {getMatchStatusColor} from '../../../../../utils/matchUtils';

interface Props {
  companyId: string;
}

export default function MatchesGraph({companyId}: Props) {
  const {t} = useTranslation();
  const td = useCallback((key: string) => t(`companyRecruitingDashboard.${key}`), [t]);

  const currentLanguage = useContext(LanguageContext);

  const [year, setYear] = useState<number>(new Date().getFullYear());

  const {data, loading, error} = useMatchesGraph(companyId, year);

  const selectStyles = useMemo(
    () => ({
      width: '113px',
      '& .MuiOutlinedInput-root': {
        backgroundColor: orangeColors[5],
        color: greenColors[700],
        borderRadius: '8px',
        '&:hover': {
          backgroundColor: orangeColors[4],
        },
        '&:hover fieldset': {
          borderColor: 'transparent',
        },
        '& fieldset': {
          borderColor: 'transparent',
        },
        '&.Mui-focused fieldset': {
          borderColor: 'transparent',
        },
        '& .MuiOutlinedInput-input': {
          caretColor: orangeColors[100],
        },
        '& .MuiSelect-icon': {
          color: greenColors[700],
          fontSize: '30px',
        },
      },
    }),
    []
  );

  const serializedData = useMemo(
    () => ({
      labels: data?.months?.map((month) => month.name[currentLanguage]),
      datasets: [
        {
          label: td('header.statuses.new'),
          data: data?.counts?.map((count) => count.accepted),
          borderColor: getMatchStatusColor(MatchStatus.New),
        },
        {
          label: td('header.statuses.interview'),
          data: data?.counts?.map((count) => count.interview),
          borderColor: getMatchStatusColor(MatchStatus.Interview),
        },
        {
          label: td('header.statuses.contacted'),
          data: data?.counts?.map((count) => count.contacted),
          borderColor: getMatchStatusColor(MatchStatus.Contacted),
        },
        {
          label: td('header.statuses.hired'),
          data: data?.counts?.map((count) => count.hired),
          borderColor: getMatchStatusColor(MatchStatus.Hired),
        },
      ],
    }),
    [data, currentLanguage, td]
  );

  const renderYearFilter = () => {
    if (!data || data.yearFilters.length === 0) {
      return null;
    }
    return (
      <TextField
        select
        sx={selectStyles}
        size='small'
        onChange={(e) => {
          setYear(parseInt(e.target.value));
        }}
        value={year}
      >
        {data.yearFilters.map((year: number) => (
          <MenuItem key={year} value={year}>
            <Typography variant='h4'>{year}</Typography>
          </MenuItem>
        ))}
      </TextField>
    );
  };

  const renderLineChart = () => {
    if (!data?.months) {
      return null;
    }
    return (
      <Box width={'100%'} height={'400px'} id='matchesGraph'>
        <LineChart data={serializedData} />
      </Box>
    );
  };

  if (error && !loading) {
    return (
      <EmpionPaper
        sx={{
          mt: '16px',
          p: '12px',
          borderRadius: '4px',
        }}
      >
        <Typography variant='h4' mb={3} align='center'>
          {td('performanceOverview.errorHandling')}
        </Typography>
      </EmpionPaper>
    );
  }

  return (
    <EmpionPaper
      sx={{
        mt: '16px',
        p: '12px',
        borderRadius: '4px',
      }}
    >
      {loading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%',
            minHeight: {xs: '30vh', sm: '40vh', md: '50vh'},
          }}
        >
          <Loading />
        </Box>
      ) : (
        <Grid container my={2}>
          <Grid item xs={3}>
            <Typography variant='h4'>{td('performanceOverview.title')}</Typography>
          </Grid>

          <Grid
            item
            mt={5}
            xs={9}
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            {renderYearFilter()}
          </Grid>

          {renderLineChart()}

          {!data?.months && !loading && (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
              }}
            >
              <Typography variant='h4' mb={3} align='center'>
                {td('performanceOverview.emptyState')}
              </Typography>
              <EmpionLogo withAnimation />
            </Box>
          )}
        </Grid>
      )}
    </EmpionPaper>
  );
}
